import {
  FDHeadlineText,
  FDPaper,
  FDStack,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDTitleText,
  FDLozenge,
  FDBox,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import fr from 'facet-rest';
import { navigateToPageInNewTab } from 'Views/Page/Content/Templates';
import useUserContext from 'Context/UserContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import SelectionConfirmationDialog
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedDecisionStepTemplate/Content/SelectionConfirmationDialog';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';

const metaOverrides = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => FDHeadlineText,
  }, overrideReadOnly)
  .nameOverride('header', {
    _component: () => FDTitleText,
    _customProps: {
      size: 'lg',
    },
  }, overrideReadOnly);

export default function FacetPlanProposalScenarioPreviewCard({
  disabled,
  scenarioPreviewResource,
}) {
  const [loading, setLoading] = React.useState(false);
  const [confirmationDialogResource, setConfirmationDialogResource] = useState(null);

  const { setIsSelecting, onAgentReview, onSelectSuccess } = useSelectScenarioContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { loadPageContentLink } = usePageContentContext();
  const { isAgent } = useUserContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const selectScenario = async (rel) => {
    // If the next up refresh relationship, is present we should submit and then go there. Otherwise, we should execute onSelectSuccess.
    setIsSelecting(true);
    try {
      const [resourceToSubmit] = await rel.link('related').get();
      await resourceToSubmit.submit();

      // If the next up refresh relationship, is present we should go there. Otherwise, we should execute onSelectSuccess, as select was actually executed.
      await scenarioPreviewResource.withRelationship(fr.relationships.nextUpRefreshStep.name,
        (refreshStepRel) => loadPageContentLink(refreshStepRel.link('related')),
        async () => {
          await onSelectSuccess(scenarioPreviewResource);
        });
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error: Select failed, please refresh and try again');
    } finally {
      setIsSelecting(false);
    }
  };

  const openConfirmationDialog = async (rel) => {
    const [confirmationResource] = await rel.link('related').get();
    setConfirmationDialogResource(confirmationResource);
  };

  const onSelectScenarioClick = async (selectScenarioRel) => {
    try {
      if (selectScenarioRel.meta('modal')) {
        await openConfirmationDialog(selectScenarioRel);
        return;
      }

      await selectScenario(selectScenarioRel);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error: Select failed, please refresh and try again');
    } finally {
      setIsSelecting(false);
    }
  };

  const onUnvalidatedScenarioClick = async (rel) => {
    setLoading(true);
    navigateToPageInNewTab(
      rel.link('related').url(),
      isAgent,
    );
    // We intentionally do not set loading to false because this operation will navigate us away from the page
    // and we should not attempt to update state on an unmounted component
  };

  const onReviewScenarioClick = async (relationship) => {
    setLoading(true);
    // This review section is displayed to both the client within page context and to the agent not within page context.
    // Therefore, we need to pass this function as a prop so we can change which component we show to the agent.
    if (isAgent) {
      await onAgentReview(scenarioPreviewResource);
      return;
    }

    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Click Review on Scenario', { eventProperties: { title: scenarioPreviewResource.attr('title') } });
    await loadPageContentLink(relationship.link('related'));
    // We intentionally do not set loading to false because this operation will navigate us away from the page
    // and we should not attempt to update state on an unmounted component
  };

  return (
    <>
      <FDPaper appearance="elevated" padding="500" width={'100%'} maxWidth="450px" minHeight="350px" {...dataAttrs('proposal-scenario-preview-card')}>
        <FDStack direction="column" spacing={1} paddingX={3} paddingY={2} alignItems={'center'} justifyContent={'space-between'} height={'100%'} overflow={'auto'}>
          <FDStack direction="column" spacing={4} width={'100%'}>
            <FDStack direction={'row'} justifyContent={'space-between'}>
              <MetaControl resource={scenarioPreviewResource} attrName={'header'} metaOverrideSet={metaOverrides} />
              {scenarioPreviewResource.attr('infoSnippets')?.resources().list().map((infoSnippet) => (
                <FDLozenge
                  label={infoSnippet.attr('name')}
                  color={infoSnippet.attr('status')}
                  alignSelf={'center'}
                  {...dataAttrs('scenario-preview-lozenge', infoSnippet, {
                    [testAttr.dataType]: infoSnippet.attr('status'),
                  })}
                />
              ))}
            </FDStack>
            <FDBox>
              <MetaControl resource={scenarioPreviewResource} attrName={'title'} metaOverrideSet={metaOverrides} />
            </FDBox>
          </FDStack>
          <FDStack direction={'row'} spacing={2} width={'100%'}>
            {scenarioPreviewResource.withRelationship(fr.relationships.selectScenario.name, (rel) => {
              const primaryButton = (<FDPrimaryActionButton
                size={'lg'}
                label={rel.meta('prompt')}
                onClick={() => onSelectScenarioClick(rel)}
                disabled={disabled}
                loading={loading}
                isFullWidth
                {...dataAttrs('scenario-preview-card-primary-button')}
              />);
              const secondaryButton = scenarioPreviewResource.withRelationship(fr.relationships.reviewScenario.name, (reviewRel) => (
                <FDSecondaryActionButton
                  size={'lg'}
                  label={reviewRel.meta('prompt')}
                  disabled={disabled}
                  loading={loading}
                  onClick={() => onReviewScenarioClick(reviewRel)}
                  isFullWidth
                  {...dataAttrs('scenario-preview-card-secondary-button')}
                />
              ), () => null);
              return (
                <>
                  {primaryButton}
                  {secondaryButton}
                </>
              );
            }, () => {
              return scenarioPreviewResource.withRelationship(fr.relationships.reviewScenario.name, (reviewRel) => (
                <FDPrimaryActionButton
                  size={'lg'}
                  label={reviewRel.meta('prompt')}
                  disabled={disabled}
                  loading={loading}
                  onClick={() => onReviewScenarioClick(reviewRel)}
                  isFullWidth
                  {...dataAttrs('scenario-preview-card-primary-button')}
                />
              ), () => null);
            })}
            {scenarioPreviewResource.withRelationship(fr.relationships.unvalidatedScenario.name, (rel) => (<FDPrimaryActionButton
              size={'lg'}
              label={rel.meta('prompt')}
              onClick={() => onUnvalidatedScenarioClick(rel)}
              disabled={disabled}
              loading={loading}
              isFullWidth
              {...dataAttrs('scenario-preview-card-primary-button')}
            />), () => null)}
          </FDStack>
        </FDStack>
      </FDPaper>
      <SelectionConfirmationDialog
        onSelectScenario={selectScenario}
        confirmationDialogResource={confirmationDialogResource}
        setConfirmationDialogResource={setConfirmationDialogResource}
      />
    </>
  );
}

FacetPlanProposalScenarioPreviewCard.defaultProps = {
  disabled: false,
};

FacetPlanProposalScenarioPreviewCard.propTypes = {
  scenarioPreviewResource: PropTypes.instanceOf(fr.Resource).isRequired,
  disabled: PropTypes.bool,
};
